<template>
  <div
    class="pviews"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    @keydown.esc.prevent="emit('close')"
  >
    <button type="button" class="pviews__catch" aria-label="닫기" @click="emit('close')" />
    <div class="pviews__panel" tabindex="-1" ref="panelEl">
      <header class="pviews__head">
        <div>
          <h2 :id="titleId">조회 · 전환</h2>
          <p class="pviews__meta">
            <strong>{{ productName || '상품을 선택하세요' }}</strong>
            <span v-if="productCode">{{ productCode }}</span>
          </p>
        </div>
        <button type="button" class="pviews__close" aria-label="조회 닫기" @click="emit('close')">
          <AppIcon icon="mdi-close" :size="20" />
        </button>
      </header>

      <label v-if="candidates.length" class="pviews__pick">
        <span>상품</span>
        <select class="sui-input" :value="productCode" @change="onPick">
          <option value="">상품을 선택하세요</option>
          <option v-for="item in candidates" :key="item.code" :value="item.code">
            {{ item.name }}
          </option>
        </select>
      </label>

      <section v-if="productCode" class="pviews__summary" data-tour="product-views-summary">
        <div>
          <span>조회수</span>
          <strong>{{ views.toLocaleString('ko-KR') }}</strong>
        </div>
        <div>
          <span>누적 판매</span>
          <strong>{{ soldCount.toLocaleString('ko-KR') }}개</strong>
        </div>
        <div>
          <span>조회→구매</span>
          <strong>{{ conversionLabel }}</strong>
        </div>
      </section>

      <p v-if="productCode" class="pviews__guide">
        조회수는 구매자가 몰 상품 상세를 연 횟수입니다. 판매는 입금 확인된 수량입니다. 조회는 많은데 판매가
        적으면 가격·썸네일·상세를 점검해 보세요.
      </p>
      <p v-else class="pviews__guide">상품을 고르면 조회수와 판매, 조회에서 구매로 이어진 비율이 나옵니다.</p>

      <footer class="pviews__actions">
        <button type="button" class="sui-btn sui-btn--ghost" @click="emit('close')">닫기</button>
        <button v-if="productCode" type="button" class="sui-btn sui-btn--primary" @click="emit('preview')">
          몰 미리보기
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'

const props = withDefaults(
  defineProps<{
    productCode: string
    productName: string
    views: number
    soldCount: number
    candidates?: { code: string; name: string }[]
  }>(),
  { candidates: () => [] },
)

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'preview'): void
  (event: 'select', code: string): void
}>()

const titleId = `pviews-${Math.random().toString(36).slice(2, 7)}`
const panelEl = ref<HTMLElement | null>(null)

const conversionLabel = computed(() => {
  const views = Math.max(0, Number(props.views) || 0)
  const sold = Math.max(0, Number(props.soldCount) || 0)
  if (views <= 0) return sold > 0 ? '—' : '0%'
  return `${Math.round((sold / views) * 1000) / 10}%`
})

function onPick(event: Event) {
  emit('select', (event.target as HTMLSelectElement).value)
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close')
}

onMounted(async () => {
  document.addEventListener('keydown', onKey)
  await nextTick()
  panelEl.value?.focus()
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKey)
})
</script>

<style lang="scss" src="./ProductViewsInsightModal.scss"></style>
